import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GuidePanel, ContextHelpButton, OnboardingTour, shouldShowInitialTour } from "../components/guide/GuideSystem";
import { pageKeyByPath, type GuidePageKey } from "../config/guideContent";
import { Icon } from "../components/ui/Icon";
import { ActionButton, AlertCard, EmptyState, ErrorState, LoadingState, MetricCard, PageHeader, SearchInput, StatusPill } from "../components/operations/OperationalComponents";
import { CommandPalette } from "../components/command/CommandPalette";
import * as Legacy from "../app/runtime";

const { localPreviewPorts, apiBaseUrl, http, getUser, setAuth, clearAuth, getErr, fmt, fmtDate, fmtDateFull, fmtTime, fmtRel, urgColor, urgLabel, todayStr, startOfDayIso, endOfDayIso, genKey, safePhone, waUrl, mapsUrl, telUrl, openExternal, BRAND_LOGO, TOK, C, EXECUTIVE_ROLES, MANAGER_ROLES, SALES_ROLES, OPS_ROLES, ADMIN_ROLES, WHATSAPP_ROLES, FOLLOW_UP_ROLES, ROLE_LABELS, roleLabel, hasAnyRole, CSS, BrandLogo, ICON_NAMES, renderIcon, Btn, Input, Card, Modal, Toast, Skeleton, Badge, StatusBadge, KPI, SH, Tabs, Row, Empty, useToast, QO, useOrders, useOrder, useCustSrch, useCust, useNotifs, useRules, useCal, useMemory, useFollowUps, useRevenue, useRisks, useStaff, useFeedback, useAssist, useHealth, useUsers, useApprovals, useAudit, useDelivery, useInvoices, useMktRpt, useCharge, useSearch, useInquiries, useGST, useFollowUpTasks, useWhatsAppConversations, useWhatsAppConversation, useWhatsAppAssignableUsers, GlobalSearch, NotifBell, PIPE, StatusPipeline, CancelModal, RescheduleModal, RiderModal, OrderCommandCenter, NewOrderForm, OrderCard, LOST_REASONS, NewInquiryForm, InquiryConversationModal, QuickActions, NAV_MAP } = Legacy;

export const AccPage = () => {
  const {
    data: invD,
    isLoading: invoicesLoading,
  } = useInvoices();

  const {
    data: ordD,
    isLoading: ordersLoading,
  } = useOrders({ limit: 100 });
  const user = getUser();
  const canViewGst = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "ACCOUNTS"].includes(user?.role || "");
  const [tab, setTab] = useState("Pending");
  const [mo, setMo] = useState(new Date().toISOString().slice(5, 7)); const [yr, setYr] = useState(new Date().getFullYear().toString());
  const financeTabs = canViewGst ? ["Pending", "Invoices", "GST"] : ["Pending", "Invoices"];
  useEffect(() => { if (!financeTabs.includes(tab)) setTab("Pending"); }, [financeTabs.join("|"), tab]);
  const {
    data: gst,
    isLoading: gstLoading,
  } = useGST(
    mo,
    yr,
    canViewGst && tab === "GST",
  );
  const { t, show } = useToast();
  const qc = useQueryClient();

  const [payOrder, setPayOrder] = useState<any>(null);
  const [payForm, setPayForm] = useState({
    amount: "",
    mode: "UPI",
    reference: "",
    evidenceNote: "",
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [historyOrder, setHistoryOrder] = useState<any>(null);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reversingPayment, setReversingPayment] = useState("");

  const openPayment = (order: any) => {
    setPayOrder(order);
    setPaymentProof(null);
    setPayForm({
      amount: ((order.balanceDuePaise || 0) / 100).toFixed(2),
      mode: "UPI",
      reference: "",
      evidenceNote: "",
    });
  };

  const openPaymentHistory = async (order: any) => {
    setHistoryOrder(order);
    setHistoryLoading(true);

    try {
      const response = await http.get(`/orders/${order.id}/payments`);
      setPaymentHistory(response.data?.data || []);
    } catch (error: any) {
      show(getErr(error), "error");
      setPaymentHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const refreshPaymentHistory = async () => {
    if (!historyOrder) return;

    const response = await http.get(
      `/orders/${historyOrder.id}/payments`,
    );

    setPaymentHistory(response.data?.data || []);
  };

  const viewPaymentEvidence = async (payment: any) => {
    try {
      const response = await http.get(
        `/payments/${payment.id}/evidence`,
      );

      const url = response.data?.data?.url;

      if (!url) {
        show("Payment proof URL was not returned", "error");
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      show(getErr(error), "error");
    }
  };

  const reversePayment = async (payment: any) => {
    if (payment.reversedAt) return;

    const reason = window.prompt(
      "Why is this payment being reversed? This is audited.",
      "",
    );

    if (!reason?.trim()) return;

    const confirmed = window.confirm(
      `Reverse payment of ${fmt(payment.amountPaise)}? This restores the order and invoice balance.`,
    );

    if (!confirmed) return;

    try {
      setReversingPayment(payment.id);

      await http.post(`/payments/${payment.id}/reverse`, {
        reason: reason.trim(),
      });

      await Promise.all([
        qc.invalidateQueries({ queryKey: ["orders"] }),
        qc.invalidateQueries({ queryKey: ["invoices"] }),
        refreshPaymentHistory(),
      ]);

      show("Payment reversed and balances restored");
    } catch (error: any) {
      show(getErr(error), "error");
    } finally {
      setReversingPayment("");
    }
  };

  const recordPayment = async () => {
    if (!payOrder) return;

    const amountPaise = Math.round(Number(payForm.amount) * 100);

    if (!Number.isFinite(amountPaise) || amountPaise <= 0) {
      show("Enter a valid payment amount", "error");
      return;
    }

    if (amountPaise > payOrder.balanceDuePaise) {
      show(
        "Payment cannot exceed the pending balance",
        "error",
      );
      return;
    }

    if (!payForm.evidenceNote.trim()) {
      show(
        "Add payment evidence / proof details",
        "error",
      );
      return;
    }

    if (paymentProof) {
      const supportedProofTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/webp",
      ];

      if (!supportedProofTypes.includes(paymentProof.type)) {
        show("Proof must be PDF, JPG, PNG, or WebP", "error");
        return;
      }

      if (paymentProof.size > 5 * 1024 * 1024) {
        show("Payment proof must not exceed 5 MB", "error");
        return;
      }
    }

    try {
      setSavingPayment(true);

      const paymentResponse = await http.post(
        "/payments/manual",
        {
          orderId: payOrder.id,
          amountPaise,
          mode: payForm.mode,
          reference:
            payForm.reference.trim() || undefined,
          evidenceNote:
            payForm.evidenceNote.trim(),
        },
        {
          headers: {
            "X-Idempotency-Key": genKey(),
          },
        },
      );

      let proofUploadFailed = false;

      if (paymentProof) {
        const paymentId = paymentResponse.data?.data?.id;

        if (!paymentId) {
          proofUploadFailed = true;
        } else {
          try {
            const body = await paymentProof.arrayBuffer();

            await http.post(
              `/payments/${paymentId}/evidence`,
              body,
              {
                headers: {
                  "Content-Type": paymentProof.type,
                  "X-File-Name": encodeURIComponent(
                    paymentProof.name,
                  ),
                },
              },
            );
          } catch {
            proofUploadFailed = true;
          }
        }
      }

      await Promise.all([
        qc.invalidateQueries({ queryKey: ["orders"] }),
        qc.invalidateQueries({ queryKey: ["invoices"] }),
      ]);

      show(
        proofUploadFailed
          ? "Payment recorded, but proof file upload failed. Open Payment History and attach/check evidence before reconciliation."
          : paymentProof
            ? "Payment recorded with proof"
            : "Payment recorded",
        proofUploadFailed ? "warning" : undefined,
      );

      setPaymentProof(null);
      setPayOrder(null);
    } catch (error: any) {
      show(getErr(error), "error");
    } finally {
      setSavingPayment(false);
    }
  };

  const exportGstr1 = () => {
    if (!gst) {
      show("GST report is not loaded yet", "error");
      return;
    }

    const csvCell = (value: unknown) => {
      const text = String(value ?? "");
      return `"${text.replace(/"/g, '""')}"`;
    };

    const rows = [
      [
        "Order Number",
        "Customer",
        "Order Date",
        "Taxable Value",
        "GST",
        "Total",
        "HSN Code",
        "GST Rate",
      ],
      ...(gst.orders || []).map((order: any) => [
        order.orderNumber,
        order.customerName,
        order.createdAt
          ? new Date(order.createdAt).toISOString().slice(0, 10)
          : "",
        (
          (
            order.basePricePaise -
            order.discountPaise +
            order.deliveryChargePaise
          ) / 100
        ).toFixed(2),
        (order.gstPaise / 100).toFixed(2),
        (order.totalPaise / 100).toFixed(2),
        gst.hsnCode || "",
        gst.gstRate ?? "",
      ]),
    ];

    const csv = rows
      .map((row: any[]) => row.map(csvCell).join(","))
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `gstr1-${yr}-${mo}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    show("GSTR-1 CSV downloaded");
  };

  const orders = ordD?.data || [], invoices = invD?.data || [];
  const pending = orders.filter((o: any) => o.balanceDuePaise > 0 && !["CANCELLED"].includes(o.status));
  const overdue = orders.filter((o: any) => o.balanceDuePaise > 0 && new Date(o.deliveryDate) < new Date() && !["DELIVERED", "COLLECTED", "CANCELLED"].includes(o.status));
  const todayCollections = orders.filter((o: any) => o.deliveryDate?.slice(0, 10) === todayStr() && (o.balanceDuePaise || 0) > 0);
  const partial = invoices.filter((i: any) => i.paymentStatus === "PARTIAL").length;
  return (
    <div>
      {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
      <PageHeader
        eyebrow="Finance control"
        title="Accounts and Collections"
        subtitle="Reconcile every payment to order, invoice, mode, reference, and proof. Verbal confirmation is not payment proof."
        actions={
          <StatusPill
            tone={
              !ordersLoading &&
              overdue.length
                ? "danger"
                : "success"
            }
          >
            {ordersLoading
              ? "… overdue"
              : `${overdue.length} overdue`}
          </StatusPill>
        }
      />
      <div className="op-grid-4" style={{ marginBottom: 20 }}>
        <MetricCard
          label="Pending"
          value={
            ordersLoading
              ? "…"
              : fmt(
                  pending.reduce(
                    (s: number, o: any) =>
                      s + o.balanceDuePaise,
                    0,
                  ),
                )
          }
          tone={C.primary}
          icon="wallet"
          detail={
            ordersLoading
              ? "Loading balances…"
              : `${pending.length} orders`
          }
        />

        <MetricCard
          label="Overdue"
          value={
            ordersLoading
              ? "…"
              : overdue.length
          }
          tone={
            !ordersLoading &&
            overdue.length
              ? C.danger
              : C.success
          }
          icon="triangle-alert"
          detail={
            ordersLoading
              ? "Loading…"
              : overdue.length
                ? "escalate now"
                : "clear"
          }
        />

        <MetricCard
          label="Today Collect"
          value={
            ordersLoading
              ? "…"
              : fmt(
                  todayCollections.reduce(
                    (s: number, o: any) =>
                      s + o.balanceDuePaise,
                    0,
                  ),
                )
          }
          tone={
            !ordersLoading &&
            todayCollections.length
              ? C.warning
              : C.success
          }
          icon="indian-rupee"
          detail={
            ordersLoading
              ? "Loading…"
              : `${todayCollections.length} delivery orders`
          }
        />

        <MetricCard
          label="Invoices"
          value={
            invoicesLoading
              ? "…"
              : invoices.length
          }
          tone={C.info}
          icon="clipboard-list"
          detail={
            invoicesLoading
              ? "Loading invoices…"
              : `${partial} partial`
          }
        />
      </div>
      {overdue.length > 0 && <AlertCard tone="danger" title={`${overdue.length} overdue balance${overdue.length > 1 ? "s" : ""}`}>Escalate before new delivery commitments. Keep payment notes and proof aligned with invoices.</AlertCard>}
      <Tabs items={financeTabs} active={tab} onChange={setTab} />
      {tab === "Pending" && (!pending.length ? <Card><EmptyState icon="check" title="No pending balances" description="All visible orders are financially clear." /></Card> : pending.map((o: any) => (
        <Card key={o.id} urgency={overdue.find((x: any) => x.id === o.id) ? C.danger : C.primary} style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ fontWeight: 700, fontSize: 14 }}>{o.orderNumber}</div><div style={{ fontSize: 12, color: C.mid }}>{o.customerName} · {fmtDateFull(o.deliveryDate)}</div></div>
            <div style={{ textAlign: "right" as any }}><div style={{ fontSize: 18, fontWeight: 900, color: C.primary }}>{fmt(o.balanceDuePaise)}</div><StatusBadge status={o.status} /></div>
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" as any, marginTop: 10 }}>
            <StatusPill tone={overdue.find((x: any) => x.id === o.id) ? "danger" : "warning"}>{overdue.find((x: any) => x.id === o.id) ? "Overdue" : "Pending"}</StatusPill>
            <StatusPill tone={o.deliveryDate?.slice(0, 10) === todayStr() ? "warning" : "neutral"}>{o.deliveryDate?.slice(0, 10) === todayStr() ? "Collect today" : "Future/previous delivery"}</StatusPill>
            <Btn size="sm" onClick={() => openPayment(o)} icon="indian-rupee">Record Payment</Btn>
            <Btn size="sm" v="ghost" onClick={() => openPaymentHistory(o)} icon="clipboard-list">Payment History</Btn>
          </div>
        </Card>
      )))}
      {tab === "Invoices" && invoices.map((inv: any) => (
        <Card key={inv.id} style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ fontWeight: 700, fontSize: 13 }}>{inv.invoiceNumber}</div><div style={{ fontSize: 12, color: C.mid }}>{inv.order?.customerName} · {inv.order?.orderNumber}</div></div>
            <div style={{ textAlign: "right" as any }}><div style={{ fontWeight: 700, fontSize: 14 }}>{fmt(inv.totalPaise || 0)}</div><StatusPill tone={inv.paymentStatus === "PAID" ? "success" : inv.paymentStatus === "PARTIAL" ? "warning" : "danger"}>{inv.paymentStatus}</StatusPill></div>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <Btn size="sm" v="secondary" onClick={async () => { try { await http.post(`/invoices/${inv.id}/send`, { channel: "whatsapp" }); show("Sent via WhatsApp"); } catch (e: any) { show(getErr(e), "error"); } }} icon="message-circle">WhatsApp</Btn>
            <Btn size="sm" v="ghost" onClick={async () => { try { await http.post(`/invoices/${inv.id}/send`, { channel: "email" }); show("Sent via email"); } catch (e: any) { show(getErr(e), "error"); } }} icon="mail">Email</Btn>
            <Btn
              size="sm"
              v="ghost"
              onClick={() =>
                openPaymentHistory({
                  id: inv.orderId,
                  orderNumber: inv.order?.orderNumber,
                })
              }
              icon="clipboard-list"
            >
              Payments
            </Btn>
          </div>
        </Card>
      ))}
      {payOrder && (
        <Modal
          title={`Record Payment · ${payOrder.orderNumber}`}
          onClose={() => setPayOrder(null)}
          width={420}
        >
          <div style={{
            marginBottom: 14,
            padding: 12,
            background: C.bg,
            borderRadius: 10,
            fontSize: 13,
          }}>
            Pending balance: <strong>{fmt(payOrder.balanceDuePaise || 0)}</strong>
          </div>

          <Input
            label="Amount"
            value={payForm.amount}
            onChange={(value: string) =>
              setPayForm((current) => ({ ...current, amount: value }))
            }
            type="number"
            required
          />

          <Input
            label="Mode"
            value={payForm.mode}
            onChange={(value: string) =>
              setPayForm((current) => ({ ...current, mode: value }))
            }
            options={["UPI", "CASH"]}
          />

          <Input
            label="Reference"
            value={payForm.reference}
            onChange={(value: string) =>
              setPayForm((current) => ({ ...current, reference: value }))
            }
            placeholder="UPI / transaction reference"
          />

          <Input
            label="Evidence / Proof Details"
            value={payForm.evidenceNote}
            onChange={(value: string) =>
              setPayForm((current) => ({
                ...current,
                evidenceNote: value,
              }))
            }
            placeholder="Cash receipt, UPI screenshot checked, bank reference, etc."
            required
          />

          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 700,
                color: C.mid,
                marginBottom: 5,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Proof File
            </label>

            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              onChange={(event) =>
                setPaymentProof(event.target.files?.[0] || null)
              }
              style={{
                width: "100%",
                padding: 10,
                border: `1.5px solid ${C.border}`,
                borderRadius: 10,
                background: C.surface,
                boxSizing: "border-box",
              }}
            />

            <div
              style={{
                fontSize: 11,
                color: C.light,
                marginTop: 5,
              }}
            >
              Optional attachment · PDF/JPG/PNG/WebP · max 5 MB.
              Evidence details above remain mandatory.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              v="ghost"
              onClick={() => setPayOrder(null)}
              sx={{ flex: 1 }}
            >
              Cancel
            </Btn>

            <Btn
              onClick={recordPayment}
              disabled={savingPayment}
              sx={{ flex: 1 }}
            >
              {savingPayment ? "Recording..." : "Record Payment"}
            </Btn>
          </div>
        </Modal>
      )}

      {historyOrder && (
        <Modal
          title={`Payment History · ${historyOrder.orderNumber || ""}`}
          onClose={() => {
            setHistoryOrder(null);
            setPaymentHistory([]);
          }}
          width={720}
        >
          {historyLoading ? (
            <LoadingState rows={3} />
          ) : !paymentHistory.length ? (
            <EmptyState
              icon="wallet"
              title="No payments"
              description="No recorded payments exist for this order."
            />
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {paymentHistory.map((payment: any) => (
                <Card
                  key={payment.id}
                  style={{
                    padding: 14,
                    opacity: payment.reversedAt ? 0.7 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: 14,
                        }}
                      >
                        {fmt(payment.amountPaise)}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: C.mid,
                          marginTop: 4,
                        }}
                      >
                        {payment.mode}
                        {payment.reference
                          ? ` · ${payment.reference}`
                          : ""}
                        {payment.createdAt
                          ? ` · ${fmtDateFull(payment.createdAt)}`
                          : ""}
                      </div>

                      {payment.evidenceNote && (
                        <div
                          style={{
                            fontSize: 12,
                            color: C.text,
                            marginTop: 6,
                          }}
                        >
                          Evidence: {payment.evidenceNote}
                        </div>
                      )}

                      {payment.evidenceFileName && (
                        <div
                          style={{
                            fontSize: 11,
                            color: C.mid,
                            marginTop: 5,
                          }}
                        >
                          Proof file: {payment.evidenceFileName}
                        </div>
                      )}

                      {payment.reversedAt && (
                        <div
                          style={{
                            fontSize: 11,
                            color: C.danger,
                            marginTop: 6,
                            fontWeight: 700,
                          }}
                        >
                          REVERSED
                          {payment.reversalReason
                            ? ` · ${payment.reversalReason}`
                            : ""}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 7,
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      {payment.evidenceKey && (
                        <Btn
                          size="sm"
                          v="ghost"
                          onClick={() =>
                            viewPaymentEvidence(payment)
                          }
                        >
                          View Proof
                        </Btn>
                      )}

                      {payment.isManual &&
                        !payment.reversedAt && (
                          <Btn
                            size="sm"
                            v="danger"
                            loading={
                              reversingPayment === payment.id
                            }
                            onClick={() =>
                              reversePayment(payment)
                            }
                          >
                            Reverse
                          </Btn>
                        )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Modal>
      )}

      {tab === "GST" && (
        <Card>
          <SH title="GST Monthly Report" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Input label="Month" value={mo} onChange={setMo} options={["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]} />
            <Input label="Year"  value={yr} onChange={setYr} options={["2024", "2025", "2026", "2027"]} />
          </div>
          {gst && <div style={{ background: C.bg, borderRadius: 12, padding: 16, marginBottom: 14 }}>{[["Total Orders", gst.totalOrders], ["Taxable Value", fmt(gst.taxableValue || 0)], ["Total GST", fmt(gst.totalGst || 0)], ["CGST", fmt(gst.cgst || 0)], ["SGST", fmt(gst.sgst || 0)], ["HSN Code", gst.hsnCode]].map(([l, v]) => (<div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}><span style={{ color: C.mid }}>{l}</span><strong>{v}</strong></div>))}</div>}
          <Btn onClick={exportGstr1} icon="clipboard-list">Export GSTR-1</Btn>
        </Card>
      )}
    </div>
  );
};

export default AccPage;
